import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { OnboardingProgress } from "@/components/onboarding/OnboardingProgress";
import { WelcomeStep } from "@/components/onboarding/WelcomeStep";
import { LevelStep } from "@/components/onboarding/LevelStep";
import { DailyLifeStep } from "@/components/onboarding/DailyLifeStep";
import { InterestsStep } from "@/components/onboarding/InterestsStep";
import { PersonalPromptStep } from "@/components/onboarding/PersonalPromptStep";
import {
  getLevelPromptModifier,
  DAILY_ACTIVITIES,
  INTERESTS,
  GOAL_LABELS,
} from "@/data/onboarding-topics";
import {
  addWord,
  createStudySet,
  addWordToStudySet,
  getOrAnalyzeGrammar,
  updateWordGrammar,
  type OnboardingWord,
} from "@/hooks/useFirestore";
import { generateVocabularyWords } from "@/hooks/useDirectApi";
import type { NativeLanguage, HebrewLevel, LearningGoal } from "@/types";

type GenerateState = "editing" | "generating" | "done" | "error";

const STEP_VARIANTS = {
  enter: (direction: number) => ({
    x: direction > 0 ? 50 : -50,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -50 : 50,
    opacity: 0,
  }),
};

/** Build the AI prompt from all onboarding inputs */
function composePrompt(
  level: HebrewLevel,
  goal: LearningGoal | undefined,
  dailyActivities: string[],
  interests: string[],
  userPrompt: string
): string {
  const parts: string[] = [];

  parts.push(getLevelPromptModifier(level));

  if (goal) {
    parts.push(`Learning goal: ${GOAL_LABELS[goal].label} — ${GOAL_LABELS[goal].description}.`);
  }

  if (dailyActivities.length > 0) {
    const labels = dailyActivities
      .map((slug) => DAILY_ACTIVITIES.find((a) => a.slug === slug)?.label)
      .filter(Boolean);
    parts.push(`Daily activities: ${labels.join(", ")}.`);
  }

  if (interests.length > 0) {
    const labels = interests
      .map((slug) => INTERESTS.find((i) => i.slug === slug)?.label)
      .filter(Boolean);
    parts.push(`Interests: ${labels.join(", ")}.`);
  }

  if (userPrompt.trim()) {
    parts.push(`Additional context from the user: ${userPrompt.trim()}`);
  }

  return parts.join("\n\n");
}

function toggleInList(list: string[], item: string): string[] {
  return list.includes(item)
    ? list.filter((s) => s !== item)
    : [...list, item];
}

export function OnboardingModal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const {
    preferences,
    loading: prefsLoading,
    setNativeLanguage,
    setHebrewLevel,
    setLearningGoal,
    setOnboardingCompleted,
    setWalkthroughCompleted,
    setOnboardingPrompt,
    setDailyActivities: saveDailyActivities,
    setInterests: saveInterests,
  } = useUserPreferences();

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);

  // Step 1: Welcome
  const [language, setLanguage] = useState<NativeLanguage>("en");

  // Step 2: Level
  const [level, setLevel] = useState<HebrewLevel | undefined>();

  // Step 3: Daily Life
  const [dailyActivities, setDailyActivities] = useState<string[]>([]);

  // Step 4: Interests
  const [interests, setInterests] = useState<string[]>([]);

  // Step 5: Goal + Prompt + Generate
  const [goal, setGoal] = useState<LearningGoal | undefined>();
  const [prompt, setPrompt] = useState("");
  const [generateState, setGenerateState] = useState<GenerateState>("editing");
  const [progressMessage, setProgressMessage] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);
  const [generatedWords, setGeneratedWords] = useState<OnboardingWord[]>([]);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // Pre-populate from saved preferences
  useEffect(() => {
    if (!prefsLoading) {
      if (preferences.nativeLanguage) setLanguage(preferences.nativeLanguage);
      if (preferences.hebrewLevel) setLevel(preferences.hebrewLevel);
      if (preferences.learningGoal) setGoal(preferences.learningGoal);
      if (preferences.dailyActivities) setDailyActivities(preferences.dailyActivities);
      if (preferences.interests) setInterests(preferences.interests);
      if (preferences.onboardingPrompt) setPrompt(preferences.onboardingPrompt);
    }
  }, [prefsLoading, preferences.nativeLanguage, preferences.hebrewLevel, preferences.learningGoal, preferences.dailyActivities, preferences.interests, preferences.onboardingPrompt]);

  if (prefsLoading || preferences.onboardingCompleted) {
    return null;
  }

  const goForward = () => {
    setDirection(1);
    setStep((s) => s + 1);
  };

  const goBack = () => {
    setDirection(-1);
    setStep((s) => s - 1);
  };

  const handleWelcomeContinue = async () => {
    await setNativeLanguage(language);
    goForward();
  };

  const handleLevelContinue = async () => {
    if (!level) return;
    await setHebrewLevel(level);
    goForward();
  };

  const handleDailyLifeContinue = async () => {
    await saveDailyActivities(dailyActivities);
    goForward();
  };

  const handleInterestsContinue = async () => {
    await saveInterests(interests);
    goForward();
  };

  const handleGoalSelect = (g: LearningGoal) => {
    setGoal(g);
  };

  const handleGenerate = async () => {
    if (!user || !level) return;

    setGenerateState("generating");
    setGenerateError(null);
    setProgressMessage("Saving your preferences...");
    setProgressPercent(0);

    try {
      // Save all preferences
      if (goal) await setLearningGoal(goal);
      await setOnboardingPrompt(prompt.trim());

      setProgressMessage("Shoresh is picking the best words for you...");
      setProgressPercent(5);

      const fullDescription = composePrompt(level, goal, dailyActivities, interests, prompt);

      // Request extra to ensure we hit 50 after any duplicates
      const generated = await generateVocabularyWords(
        "Personal Vocabulary",
        fullDescription,
        55,
        true
      );

      const words: OnboardingWord[] = generated
        .map((w) => ({
          hebrew: w.hebrew,
          english: w.english,
          category: w.category || "Uncategorized",
        }))
        .slice(0, 50);

      setProgressMessage(`Got ${words.length} words! Organizing into study sets...`);
      setProgressPercent(30);

      // Group words by category
      const wordsByCategory = new Map<string, OnboardingWord[]>();
      for (const word of words) {
        const existing = wordsByCategory.get(word.category) || [];
        existing.push(word);
        wordsByCategory.set(word.category, existing);
      }

      // Save words and create study sets per category
      // TODO: Russian support hidden — re-enable by checking language === "ru"
      const translationLang = "en";
      const wordsForGrammar: { wordId: string; hebrew: string }[] = [];
      let savedCount = 0;

      for (const [category, categoryWords] of wordsByCategory) {
        setProgressMessage(`Creating study set: ${category}...`);

        const studySet = await createStudySet(
          user.uid,
          category,
          `Starter vocabulary — ${category}`
        );

        for (const word of categoryWords) {
          const translation = translationLang === "en" ? word.english : word.english;
          const wordId = await addWord(
            user.uid,
            word.hebrew,
            translation,
            translationLang as "en" | "ru",
            [word.category]
          );
          await addWordToStudySet(user.uid, studySet.id, wordId);
          wordsForGrammar.push({ wordId, hebrew: word.hebrew });
          savedCount++;

          // Progress from 35% to 90% as words are saved
          const saveProgress = 35 + Math.round((savedCount / words.length) * 55);
          setProgressPercent(saveProgress);
          setProgressMessage(`Adding words to your word bank (${savedCount}/${words.length})...`);
        }
      }

      setProgressMessage("Almost done — setting up your account...");
      setProgressPercent(95);

      // Mark onboarding as completed, enable walkthrough tour
      await setWalkthroughCompleted(false);
      await setOnboardingCompleted(true);

      setGeneratedWords(words);
      setGenerateState("done");

      // Refresh UI data immediately
      queryClient.invalidateQueries({ queryKey: ["studySets"] });
      queryClient.invalidateQueries({ queryKey: ["words"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });

      // Enrich grammar in background
      enrichGrammarBackground(user.uid, wordsForGrammar);
    } catch (err: any) {
      console.error("Onboarding generation failed:", err);
      setGenerateError(err.message || "Failed to generate vocabulary");
      setGenerateState("error");
    }
  };

  const handleRetry = () => {
    setGenerateState("editing");
    setGenerateError(null);
  };

  const handleSkip = async () => {
    await setWalkthroughCompleted(false);
    await setOnboardingCompleted(true);
  };

  const handleFinish = async () => {
    await setWalkthroughCompleted(false);
    await setOnboardingCompleted(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      <motion.div
        className="relative z-10 w-full max-w-md mx-4 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          title="Skip onboarding"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-6">
          <OnboardingProgress currentStep={step} totalSteps={5} />
        </div>

        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={STEP_VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            {step === 0 && (
              <WelcomeStep
                displayName={user?.displayName ?? null}
                photoURL={user?.photoURL ?? null}
                selectedLanguage={language}
                onSelectLanguage={setLanguage}
                onContinue={handleWelcomeContinue}
              />
            )}

            {step === 1 && (
              <LevelStep
                selectedLevel={level}
                onSelectLevel={setLevel}
                onContinue={handleLevelContinue}
                onBack={goBack}
              />
            )}

            {step === 2 && (
              <DailyLifeStep
                selected={dailyActivities}
                onToggle={(slug) => setDailyActivities(toggleInList(dailyActivities, slug))}
                onContinue={handleDailyLifeContinue}
                onBack={goBack}
              />
            )}

            {step === 3 && (
              <InterestsStep
                selected={interests}
                onToggle={(slug) => setInterests(toggleInList(interests, slug))}
                onContinue={handleInterestsContinue}
                onBack={goBack}
              />
            )}

            {step === 4 && level && (
              <PersonalPromptStep
                level={level}
                selectedGoal={goal}
                prompt={prompt}
                onSelectGoal={handleGoalSelect}
                onChangePrompt={setPrompt}
                onGenerate={handleGenerate}
                onBack={goBack}
                state={generateState}
                progressMessage={progressMessage}
                progressPercent={progressPercent}
                generatedWords={generatedWords}
                error={generateError}
                onRetry={handleRetry}
                onSkip={handleSkip}
                onFinish={handleFinish}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

/** Fire-and-forget grammar enrichment for onboarding words */
async function enrichGrammarBackground(
  userId: string,
  words: { wordId: string; hebrew: string }[]
) {
  try {
    const grammarResults = await Promise.allSettled(
      words.map((w) => getOrAnalyzeGrammar(w.hebrew))
    );

    for (let i = 0; i < words.length; i++) {
      const result = grammarResults[i];
      if (result.status === "fulfilled" && result.value) {
        const grammar = result.value;
        await updateWordGrammar(
          userId,
          words[i].wordId,
          grammar.wordType,
          grammar.nounInfo,
          grammar.verbInfo,
          grammar.adjectiveInfo
        );
      }
    }
  } catch (err) {
    console.warn("Background grammar enrichment failed:", err);
  }
}
